// eslint-disable-next-line
import React, {
  Component,
  Context,
  PropsWithChildren,
  ReactNode,
  createContext,
  useContext,
} from 'react';
import { Theme, createStyles, withStyles } from '@material-ui/core/styles';

import Button from '@material-ui/core/Button';
import { Nullable } from 'components/utils';
import Snackbar from '@material-ui/core/Snackbar';

export enum SnackbarType {
  error = 'error',
  success = 'success',
  info = 'info',
}

type Props = PropsWithChildren<{
  classes: any;
}>;

interface State {
  message: string;
  showMessage: boolean;
  actionName: string;
  type: SnackbarType;
  detailCRM: Nullable<string>;
  autoHide: boolean;
  // eslint-disable-next-line
  action?: (context: SnackbarContextValue & { className: string }) => ReactNode;
}

type ShowActionParams = {
  message: string;
  actionName?: string;
  type?: SnackbarType;
  detailCRM?: Nullable<string>;
  autoHide?: boolean;
  action?: State['action'];
};

export type ShowAction = (params: ShowActionParams) => void;

export type HideAction = () => void;

export type SnackbarContextValue = {
  show: ShowAction;
  hide: HideAction;
};

// createContext matches the shape that the consumers expect!
export const SnackbarContext: Context<SnackbarContextValue> = createContext({
  show: ({ message, actionName, type }) => {},
  hide: () => {},
});

const styles = (theme: Theme) =>
  createStyles({
    btnSuccess: {
      color: theme.palette.success.main,
      '&:hover': {
        backgroundColor: theme.palette.success.light,
      },
    },
    btnInfo: {
      color: theme.palette.info.main,
      '&:hover': {
        backgroundColor: theme.palette.info.light,
      },
    },
  });

const SNACKBAR_AUTO_HIDE_DURATION = 6000;

class SnackbarProviderComponent extends Component<Props, State> {
  state: State = {
    message: 'Error',
    showMessage: false,
    actionName: 'CERRAR',
    type: SnackbarType.error,
    detailCRM: null,
    autoHide: false,
  };

  /**
   * Set new state for the snackbar
   */
  show: ShowAction = ({
    message,
    actionName = 'CERRAR',
    type = SnackbarType.error,
    detailCRM,
    autoHide = false,
    action,
  }: ShowActionParams): void => {
    this.setState({
      message,
      detailCRM,
      actionName,
      type,
      showMessage: true,
      autoHide,
      action,
    });
  };

  /**
   * Hide the snackbar
   */
  hide: HideAction = (): void => {
    this.setState({
      showMessage: false,
      detailCRM: null,
    });
  };

  contextValue: SnackbarContextValue = {
    show: this.show,
    hide: this.hide,
  };

  /**
   * Used internally to detect the reason before closing the snackbar
   */
  close = (event: Object, reason: string): void => {
    // Do nothing when clickaway and is an error
    if (reason === 'clickaway' && this.state.type === 'error') return;
    this.hide();
  };

  showMore = () => {
    this.setState({
      message: this.state.detailCRM || this.state.message,
      detailCRM: null,
    });
  };

  /**
   * Action button
   */
  actionBtnClassName = () =>
    this.state.type === 'success'
      ? this.props.classes.btnSuccess
      : this.state.type === 'info'
      ? this.props.classes.btnInfo
      : undefined;

  actionBtn = () => (
    <>
      {this.state.detailCRM && (
        <Button color="primary" size="small" onClick={this.showMore}>
          VER MAS
        </Button>
      )}

      {this.state?.action ? (
        this.state.action({
          ...this.contextValue,
          className: this.actionBtnClassName(),
        })
      ) : (
        <Button
          color="primary"
          size="small"
          onClick={this.hide}
          className={this.actionBtnClassName()}
        >
          {this.state.actionName}
        </Button>
      )}
    </>
  );

  /**
   * Message template
   */
  message = () => <span id="snackbar-content">{this.state.message}</span>;

  render(): ReactNode {
    const { showMessage, type, autoHide } = this.state;
    const autoHideDuration = autoHide
      ? SNACKBAR_AUTO_HIDE_DURATION
      : type === 'success'
      ? SNACKBAR_AUTO_HIDE_DURATION
      : undefined;

    return (
      <SnackbarContext.Provider value={this.contextValue}>
        {this.props.children}
        <Snackbar
          action={this.actionBtn()}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          open={showMessage}
          onClose={this.close}
          message={this.message()}
          autoHideDuration={autoHideDuration}
        />
      </SnackbarContext.Provider>
    );
  }
}

export const SnackbarProvider = withStyles(styles)(SnackbarProviderComponent);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
